<script setup> 
    // Imports
    import { ref, onMounted, onUnmounted, computed, defineProps } from 'vue';
    import { useRoute } from 'vue-router';
    import { Action } from '@/api/action';
    import Data from '@/api/data';
    import DefaultLayout from '@/layouts/Layout.vue';
    import LoadingAnimation3 from '@/components/loadingIcons/LoadingAnimation3.vue';
    import InteractiveXetaSence from '@/components/common/InteractiveXetaSence.vue';

    // When embedded in DashboardPage, we don't wrap in DefaultLayout (parent already provides it)
    defineProps({
        embedded: { type: Boolean, default: false }
    });

    // References
    const loading = ref(true);
    const errorMessage = ref("");
    const config = ref(null);
    const XetasensePlaceholders = ref([]);
    const updateSuccess = ref(false);

    // Variables
    let requestID = null;
    let resultStream = null;
    let updatedConfig = null;
    const route = useRoute();
    const dataID = route.params.dataID;

    // Lifecycle Hooks
    onMounted(async () => {
        setupListener();
        const response = await Action.get(`?dataID=${dataID}&actionClass=Xetasense&payload=ListConfigInfo`);
        if (response.status === 'success') {
            requestID = response.details.action_id;
        } else {
            console.log(response);
        }
    });

    onUnmounted(() => {
        resultStream.close();
    });

    // Methods
    const setupListener = async () => {
        loading.value = true;
        errorMessage.value = "";
        
        // Set up response Stream
        resultStream = await Data.stream(dataID, 'action_result');

        resultStream.addEventListener('added', async (event) => {
            const addedData = JSON.parse(event.data);
            for (const result of addedData) {
                if (result.action_class === 'Xetasense' && result.id === requestID) {
                    if (result.status === 'OK' && result.action_type === 'Read') {
                        // Parse the payload if it's a string, otherwise use it as is
                        config.value = JSON.parse(result.payload);
                        loading.value = false;

                    } else if (result.status === 'OK' && result.action_type === 'Update') {
                        config.value = updatedConfig;
                        // Set update success to true
                        updateSuccess.value = true;
                        // Reset after a short delay
                        setTimeout(() => {
                            updateSuccess.value = false;
                        }, 100);
                        
                    }
                }
            }

            if (loading.value) {
                setTimeout(() => {
                    errorMessage.value = "Failed to connect";
                }, 30000);
            }
        });
    };

    const handleConfigChange = async (newDetails) => {
        updatedConfig = config.value;

        const bus = updatedConfig.busses.find(bus => bus.bus_id === newDetails.bus_id);
        bus.channels = newDetails.channels;

        const requestBody = JSON.stringify({
            dataID: dataID,
            actionClass: 'Xetasense',
            payload: {
                UpdateConfig: JSON.stringify(updatedConfig)
            }
        });

        const response = await Action.put('', requestBody);

        if (response.status === 'success') {
            requestID = response.details.action_id;
            
            // If the bus was a placeholder, remove it from the XetasensePlaceholders array
            if (XetasensePlaceholders.value.includes(newDetails.bus_id)) {
                XetasensePlaceholders.value = XetasensePlaceholders.value.filter(id => id !== newDetails.bus_id);
            }
        } else {
            console.error('Failed to update config');
        }
    };

    const handleRemoveBus = (bus) => {
        // If the bus is only a placeholder, remove it from the XetasensePlaceholders array
        if (XetasensePlaceholders.value.includes(bus.bus_id)) {
            XetasensePlaceholders.value = XetasensePlaceholders.value.filter(id => id !== bus.bus_id);
        }

        // If the bus has actual configurations, set it to default values and update the config
        else {
            for (const channel in bus.channels) {
                bus.channels[channel].scale.min = 0;
                bus.channels[channel].scale.max = 0;
                bus.channels[channel].scale.offset = 0;
                bus.channels[channel].scale.unit = "";
                bus.channels[channel].description = "";
                bus.channels[channel].enabled = false;
                bus.channels[channel].report_interval = 60;
            }

            handleConfigChange(bus);
        }
    };

    const handleAddBus = (busId) => {
        XetasensePlaceholders.value.push(busId);
    };

    const activeBuses = computed(() => {
        if (!config.value) {
            return [];
        }
        
        // Get buses from config that are either placeholders or have configuration
        const configuredBuses = config.value.busses.filter(bus => {
            return XetasensePlaceholders.value.includes(bus.bus_id) || 
                Object.values(bus.channels).some(channel => {
                    const scale = channel.scale;
                    return scale.min !== 0 || scale.max !== 0 || scale.offset !== 0;
                });
        });
        
        // Add placeholder buses that might not exist in config yet
        const placeholderBuses = XetasensePlaceholders.value
            .filter(busId => !configuredBuses.some(bus => bus.bus_id === busId))
            .map(busId => {
                // Find the bus in config.value.busses or create a basic structure
                const existingBus = config.value.busses.find(bus => bus.bus_id === busId);
                if (existingBus) {
                    return existingBus;
                }
                
                // Create a basic bus structure if it doesn't exist
                return {
                    bus_id: busId,
                    channels: {
                        "1": { scale: { min: 0, max: 0, offset: 0, unit: "" }, description: "", enabled: false, report_interval: 60 },
                        "2": { scale: { min: 0, max: 0, offset: 0, unit: "" }, description: "", enabled: false, report_interval: 60 },
                        "3": { scale: { min: 0, max: 0, offset: 0, unit: "" }, description: "", enabled: false, report_interval: 60 },
                        "4": { scale: { min: 0, max: 0, offset: 0, unit: "" }, description: "", enabled: false, report_interval: 60 },
                        "5": { scale: { min: 0, max: 0, offset: 0, unit: "" }, description: "", enabled: false, report_interval: 60 },
                        "6": { scale: { min: 0, max: 0, offset: 0, unit: "" }, description: "", enabled: false, report_interval: 60 },
                        "7": { scale: { min: 0, max: 0, offset: 0, unit: "" }, description: "", enabled: false, report_interval: 60 },
                        "8": { scale: { min: 0, max: 0, offset: 0, unit: "" }, description: "", enabled: false, report_interval: 60 },
                        "9": { scale: { min: 0, max: 0, offset: 0, unit: "" }, description: "", enabled: false, report_interval: 60 },
                        "10": { scale: { min: 0, max: 0, offset: 0, unit: "" }, description: "", enabled: false, report_interval: 60 },
                        "11": { scale: { min: 0, max: 0, offset: 0, unit: "" }, description: "", enabled: false, report_interval: 60 },
                        "12": { scale: { min: 0, max: 0, offset: 0, unit: "" }, description: "", enabled: false, report_interval: 60 }
                    }
                };
            });
        
        return [...configuredBuses, ...placeholderBuses];
    });

    const inactiveBuses = computed(() => {
        const active = activeBuses.value.map(bus => bus.bus_id);
        return ['can0', 'can1', 'can2'].filter(id => !active.includes(id));
    });
</script>

<template>
    <DefaultLayout
        v-if="!embedded"
        :dataID="dataID"
    >
        <div v-if="loading" class="flex flex-col items-center mt-[100px] h-full w-full">
            <h2 class="text-xl font-semibold text-gray-700 mb-8 underline">Please make sure the XetaLynk™ gateway is turned on</h2>
            <LoadingAnimation3 v-if="errorMessage === ''" size="100px" message="Requesting XetaSense™ Information..." />
            <div v-else class="flex flex-col items-center">
                <h2 class="text-sm text-gray-700">{{ errorMessage }}</h2>
                <button class="default-button" @click="setupListener()">Try Again</button>
            </div>
        </div>
        <div v-else class="mt-[50px] flex flex-col items-center">
            <!-- No XetaSense™ devices connected -->
            <h2 v-if="activeBuses.length === 0" class="text-xl font-semibold text-gray-700 mb-8">No XetaSense™ devices connected</h2>

            <!-- Add buttons for inactive buses -->
            <div class="flex gap-4 mb-8">
                <button
                    v-for="busId in inactiveBuses"
                    :key="busId"
                    type="button"
                    @click="handleAddBus(busId)"
                    class="default-button"
                >
                    Add XetaSense™ {{ busId }}
                </button>
            </div>

            <!-- Active buses -->
            <div class="grid grid-cols-1 gap-8 w-fit">
                <div v-for="bus in activeBuses" :key="bus.bus_id" class="bg-gray-50 rounded-lg p-6">
                    <div class="flex justify-between items-center mb-4">
                        <h2 class="text-xl font-semibold text-gray-700">{{ bus.bus_id }}</h2>
                        <button
                            type="button"
                            @click="handleRemoveBus(bus)"
                            class="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                        >
                            Remove
                        </button>
                    </div>
                    <InteractiveXetaSence 
                        :configDetails="bus" 
                        :updateSuccess="updateSuccess"
                        @changedDetails="handleConfigChange"
                    />
                </div>
            </div>
        </div>
    </DefaultLayout>
    <template v-else>
        <div v-if="loading" class="flex flex-col items-center mt-[100px] h-full w-full">
            <h2 class="text-xl font-semibold text-gray-700 mb-8 underline">Please make sure the XetaLynk™ gateway is turned on</h2>
            <LoadingAnimation3 v-if="errorMessage === ''" size="100px" message="Requesting XetaSense™ Information..." />
            <div v-else class="flex flex-col items-center">
                <h2 class="text-sm text-gray-700">{{ errorMessage }}</h2>
                <button class="default-button" @click="setupListener()">Try Again</button>
            </div>
        </div>
        <div v-else class="mt-[50px] flex flex-col items-center">
            <h2 v-if="activeBuses.length === 0" class="text-xl font-semibold text-gray-700 mb-8">No XetaSense™ devices connected</h2>
            <div class="flex gap-4 mb-8">
                <button
                    v-for="busId in inactiveBuses"
                    :key="busId"
                    type="button"
                    @click="handleAddBus(busId)"
                    class="default-button"
                >
                    Add XetaSense™ {{ busId }}
                </button>
            </div>
            <div class="grid grid-cols-1 gap-8 w-fit">
                <div v-for="bus in activeBuses" :key="bus.bus_id" class="bg-gray-50 rounded-lg p-6">
                    <div class="flex justify-between items-center mb-4">
                        <h2 class="text-xl font-semibold text-gray-700">{{ bus.bus_id }}</h2>
                        <button
                            type="button"
                            @click="handleRemoveBus(bus)"
                            class="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                        >
                            Remove
                        </button>
                    </div>
                    <InteractiveXetaSence 
                        :configDetails="bus" 
                        :updateSuccess="updateSuccess"
                        @changedDetails="handleConfigChange"
                    />
                </div>
            </div>
        </div>
    </template>
</template>